import WITHDRAWAL_OPTIONS from "features/dashboard/domain/model/withdrawalOptions";
import { useForm } from "@tanstack/react-form";
import { KroButton } from "features/components/buttons/kroButton";
import { CampaignModelMapper } from "features/campaign/domain/models/campaignModel";
import { WithdrawalTile } from "./WithdrawalTile";
import { WithdrawalType } from "features/dashboard/domain/enums/withdrawalType.enum";

export interface WithdrawalOptionsForm {
  withdrawalType: WithdrawalType;
}

export function WithdrawalOptions({
  openWithdrawalOverview,
  closeWithdrawalOptions,
  campaign,
}: {
  openWithdrawalOverview: (withdrawalType: string) => void;
  closeWithdrawalOptions: () => void;
  campaign: CampaignModelMapper;
}) {
  const form = useForm<WithdrawalOptionsForm>({
    defaultValues: {
      withdrawalType: WithdrawalType.none,
    },
  });

  const amountWithdrawable =
    campaign.amountRaised * campaign.withdrawalCapPercentage;

  const hasReachedPartialWithdrawalLimit =
    campaign?.totalAmountWithdrawn >= amountWithdrawable;

  if (
    hasReachedPartialWithdrawalLimit &&
    form.getFieldValue("withdrawalType") === WithdrawalType.partial
  ) {
    form.setFieldValue("withdrawalType", WithdrawalType.none);
  }

  return (
    <>
      {hasReachedPartialWithdrawalLimit && (
        <div className="mt-5 flex items-center bg-[#FFFBED] p-3 px-10">
          <p className="font-inter text-[14px] text-[#7E4510]">
            You've reached the limit for partial withdrawals on this campaign.
          </p>
        </div>
      )}
      <div className="mx-5 mb-3 mt-[30px] lg:mx-10 lg:mb-10">
        <p className="font-inter text-[18px] font-medium leading-[24px] tracking-[-0.5%] text-[#404040] lg:leading-[26px] lg:text-neutral-900">
          How would you like your withdrawal?
        </p>
        <form.Subscribe>
          {(state) => (
            <div className="mt-4 flex flex-col gap-[19px] lg:mt-6">
              {WITHDRAWAL_OPTIONS.map((option, index) => {
                const isPartialAndDisabled =
                  option.type === WithdrawalType.partial &&
                  (hasReachedPartialWithdrawalLimit ||
                    campaign?.status === "ended");
                return (
                  <WithdrawalTile
                    key={index}
                    form={form}
                    name={option.name}
                    description={
                      option.type === WithdrawalType.complete &&
                      campaign?.status === "ended"
                        ? "Cash it all out"
                        : option.description
                    }
                    withdrawalType={option.type as WithdrawalType}
                    isSelected={state.values.withdrawalType === option.type}
                    disabled={isPartialAndDisabled}
                  />
                );
              })}
            </div>
          )}
        </form.Subscribe>
        <form.Subscribe>
          {(state) => {
            const isDisabled =
              state.values.withdrawalType === WithdrawalType.none;

            return (
              <KroButton
                title="Continue"
                isDisabled={isDisabled}
                onclick={() => {
                  closeWithdrawalOptions();
                  openWithdrawalOverview(state.values.withdrawalType ?? "");
                }}
                className="mt-5 h-[60px] max-h-full w-full"
              />
            );
          }}
        </form.Subscribe>
      </div>
    </>
  );
}
